import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBriefcase, FaHome, FaPassport, FaFileAlt } from 'react-icons/fa';
import { useInView } from '../hooks/useInView';
import { practiceAreas } from '../data/practiceAreas';
import { teamMembers } from '../data/teamMembers';
import AvatarPlaceholder from './ui/AvatarPlaceholder';

const HomePage: React.FC = () => {
  useEffect(() => {
    document.title = 'Janssen Law Professional Corporation | Toronto Law Firm';
  }, []);

  const heroSection = useInView();
  const practiceSection = useInView();
  const aboutSection = useInView();
  const teamSection = useInView();
  const ctaSection = useInView();

  const areaIcons: Record<string, React.ReactNode> = {
    'business-law': <FaBriefcase />,
    'real-estate': <FaHome />,
    'immigration': <FaPassport />,
    'wills-estates': <FaFileAlt />,
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero__overlay" />
        <div
          ref={heroSection.ref}
          className={`hero__content ${heroSection.isVisible ? 'visible' : ''}`}
        >
          <p className="hero__label">Toronto Law Firm</p>
          <h1 className="hero__title">
            Janssen Law<br />
            Professional Corporation
          </h1>
          <p className="hero__subtitle">
            Business, Real Estate, Immigration, Wills & Estates
          </p>
          <p className="hero__tagline">No-Nonsense, Results-Oriented Legal Counsel</p>
          <div className="hero__buttons">
            <Link to="/contact" className="btn-primary">
              Contact Us <FaArrowRight />
            </Link>
            <Link to="/about" className="btn-secondary">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="home-practice">
        <div
          ref={practiceSection.ref}
          className={`home-practice__inner ${practiceSection.isVisible ? 'visible' : ''}`}
        >
          <div className="home-practice__header">
            <h2>Areas of Practice</h2>
            <p>We provide comprehensive legal services across four core practice areas.</p>
          </div>
          <div className="home-practice__grid">
            {practiceAreas.map((area) => (
              <Link
                key={area.slug}
                to={`/practice-areas/${area.slug}`}
                className="practice-card"
              >
                <div
                  className="practice-card__image"
                  style={{ backgroundImage: `url(${area.backgroundImage})` }}
                >
                  <div className="practice-card__overlay" />
                </div>
                <div className="practice-card__content">
                  <span className="practice-card__icon">{areaIcons[area.slug]}</span>
                  <h3>{area.title}</h3>
                  <p>{area.shortDescription}</p>
                  <span className="practice-card__link">
                    Learn More <FaArrowRight />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="home-about">
        <div
          ref={aboutSection.ref}
          className={`home-about__inner ${aboutSection.isVisible ? 'visible' : ''}`}
        >
          <div className="home-about__content">
            <h2>About Our Firm</h2>
            <div className="home-about__quote">
              <p>No-Nonsense, Results-Oriented Law Firm.</p>
              <p>Charlotte Janssen oversees every case.</p>
            </div>
            <p className="home-about__description">
              Established in 1996, Janssen Law Professional Corporation has been serving
              clients with dedicated legal counsel for nearly three decades. With international
              experience spanning Toronto, S&atilde;o Paulo, and London, we bring a global
              perspective to local legal matters.
            </p>
            <Link to="/about" className="btn-outline-white">
              Learn More About Us <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Team Preview */}
      <section className="home-team">
        <div
          ref={teamSection.ref}
          className={`home-team__inner ${teamSection.isVisible ? 'visible' : ''}`}
        >
          <div className="home-team__header">
            <h2>Our Team</h2>
            <p>Experienced professionals dedicated to your success.</p>
          </div>
          <div className="home-team__grid">
            {teamMembers.map((member) => (
              <div key={member.name} className="home-team__card">
                <AvatarPlaceholder initials={member.initials} size={100} />
                <h3>{member.name}</h3>
                <span className="home-team__title">{member.title}</span>
                {member.practiceAreas.length > 0 && (
                  <div className="home-team__areas">
                    {member.practiceAreas.slice(0, 3).map((area) => (
                      <span key={area}>{area}</span>
                    ))}
                    {member.practiceAreas.length > 3 && (
                      <span>+{member.practiceAreas.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="home-team__cta">
            <Link to="/team" className="btn-secondary">
              Meet Our Team <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta">
        <div
          ref={ctaSection.ref}
          className={`home-cta__inner ${ctaSection.isVisible ? 'visible' : ''}`}
        >
          <h2>Ready to Discuss Your Legal Needs?</h2>
          <p>Get in touch with our team for a consultation.</p>
          <div className="home-cta__buttons">
            <Link to="/contact" className="btn-primary">
              Get in Touch <FaArrowRight />
            </Link>
            <a href="tel:+14169291103" className="btn-secondary">
              Call (416) 929-1103
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
